import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ManageConstraintService } from '../api/manage-constraint.service';
import { Constraint } from '../model/constraint';
import { NotificationsService } from '../api/notifications.service';
import { RpcService } from '../api/rpc.service';
import { catchError, concatMap, toArray, finalize } from 'rxjs/operators';
import { of, from, timer } from 'rxjs';

@Component({
  selector: 'app-add-constraint',
  templateUrl: './add-constraint.component.html',
  styleUrls: ['./add-constraint.component.scss']
})
export class AddConstraintComponent {
  form: FormGroup;
  formData: any = null;
  itemConstraintType: Array<string> = [
    'FORBIDDEN_DISCOUNT_PRICE',
    'FORBIDDEN_STORE_PRICE',
    'SEASON_SALES_EXCLUDED'
  ];
  userId: string;
  permissions: string[];
  authorized_countries:  string[];
  isSending: boolean = false;

  constructor(private fb: FormBuilder, private manageConstraintService: ManageConstraintService, private notificationsService: NotificationsService, private rpcService: RpcService,) {
    this.rpcService.getCurrentUser()
    .subscribe((user) => {
       this.userId = user.login;
       this.permissions = user.permissions;
       this.authorized_countries = user.authorized_countries;
     });

    this.form = this.fb.group({
      constraintType: ['', Validators.required],
      modelsList: ['', [Validators.required]],
      tierCode: ['', [Validators.required, Validators.pattern(/^\d+_\d+_\d+$/)]], // Validation numérique
      comment: [''],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required]
    });
  }

  getModels(model: string): any {
    const modelList = model.split(',');
    return modelList.map(model => {
      const code = model.trim();
      return {
        type: 'MODEL',
        code: parseInt(code)
      };
    });
  }

  formatDate = (date: string): string => {
    const d = new Date(date);
    return d.toISOString().slice(0, 19);
  };

  private exportConstraintsToCSV(results: any[]) {
    if (!results || results.length === 0) { return; }
    
    const columns = [
      'id_item_constraint',
      'code',
      'tier_type',
      'tier_code',
      'tier_sub_code',
      'item_constraint_type',
      'start_date',
      'end_date',
      'comment',
      'creation_date',
      'update_date',
      'created_by'
    ];

    const header = columns.join(';');

    const rows = results.map(obj =>
      columns.map(c => {
        const val = obj[c] ?? '';
        const str = String(val).replace(/"/g, '""');
        return str.includes(';') ? `"${str}"` : str;
      }).join(';')
    );

    const csv = [header, ...rows].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'summary.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  onSubmit() {
    if (this.form.valid) {
      this.formData = this.form.value;
      this.isSending= true;

      this.notificationsService.success('Constraints creation in progress...', '');

      const chunkArray = <T>(array: T[], size: number): T[][] => {
        return Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
          array.slice(i * size, i * size + size)
        );
      };

      const modelChunks = chunkArray(this.getModels(this.formData.modelsList), 60);
      from(modelChunks)
        .pipe(
          concatMap((modelsChunk:any, index) => {
            const constraint: Constraint = {
              subjects: modelsChunk,
              tier_type: parseInt(this.formData.tierCode.split('_')[0]),
              tier_code: parseInt(this.formData.tierCode.split('_')[1]),
              tier_sub_code: parseInt(this.formData.tierCode.split('_')[2]),
              item_constraint_type: this.formData.constraintType,
              start_date: this.formatDate(this.formData.startDate),
              end_date: this.formatDate(this.formData.endDate),
              comment: (this.formData.comment) ? this.formData.comment : 'Added from front'
            };

            return timer(index * 100).pipe(
              concatMap(() =>
                this.manageConstraintService.addConstraint(constraint).pipe(
                  catchError((error) => {
                    this.notificationsService.error('Error adding constraint', `${error.error?.error_description?.[0] || 'Unknown error'}`);
                    return of(null);
                  })
                )
              )
            );
          }),
          toArray(),
          finalize(() => {
            this.isSending= false;
          })
        ).subscribe((results) => {
          if (results.every(r => r !== null)) {
            this.notificationsService.success('All constraints added', '');
            const flattened = results.filter(r => r !== null).flat(); 
            this.exportConstraintsToCSV(flattened);

            this.form.reset();
          } else {
            this.notificationsService.error("Some constraints haven't be addded", '');
          }
        });
    }
  }
}
